import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { useTranslation } from '../hooks/useTranslation';
import { motion, AnimatePresence } from 'framer-motion';
import { useMusic } from '../hooks/useMusic';

// Modals
import { BuildingModal } from '../components/Base/BuildingModal';
import { BarracksModal } from '../components/Base/BarracksModal';
import { ForgeModal } from '../components/Base/ForgeModal';
import { TempleModal } from '../components/Base/TempleModal';
import { MageTowerModal } from '../components/Base/MageTowerModal';
import { RaidModal } from '../components/Base/RaidModal';
import { BaseTutorial } from '../components/BaseTutorial';
import { ExtendedTutorial } from '../components/ExtendedTutorial';


export const BasePage: React.FC = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  /* baseRace is not currently used in the UI */

  const {
    gold,
    resources,
    liberation,
    buildings,
    fighters,
    maxFighters,
    regenerateFightersHP,
    hasSeenBaseTutorial,
    setHasSeenBaseTutorial,
    extendedTutorialStep,
    extendedTutorialPhase,
    setExtendedTutorialStep,
    setExtendedTutorialPhase,
    raidStats,
    potions,
    collectTaxes,
    getTaxAmount,
    getTaxCooldown,
    getAttackCooldown,
    getAttackInfo,
    generateBaseAttackEnemies,
    resetAttackTimer,
    musicEnabled,
    musicVolume,
  } = useGameStore();

  // Фоновая музыка для базы и зданий
  useMusic({ type: 'menu', volume: musicVolume, enabled: musicEnabled });

  const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);
  const [showRaid, setShowRaid] = useState(false);
  const [showTaxHouse, setShowTaxHouse] = useState(false);
  const [showOrcAttack, setShowOrcAttack] = useState(false);
  const [taxCooldown, setTaxCooldown] = useState(0);
  const [attackCooldown, setAttackCooldown] = useState(0);

  // Регенерация HP (1 HP / мин)
  React.useEffect(() => {
    const interval = setInterval(() => {
      regenerateFightersHP();
    }, 30000); // Проверяем каждые 30 сек
    return () => clearInterval(interval);
  }, [regenerateFightersHP]);

  // Обновление таймера налогов
  React.useEffect(() => {
    const updateCooldown = () => {
      setTaxCooldown(getTaxCooldown());
    };
    updateCooldown();
    const interval = setInterval(updateCooldown, 1000); // Обновляем каждую секунду
    return () => clearInterval(interval);
  }, [getTaxCooldown]);

  // Обновление таймера нападения орков
  React.useEffect(() => {
    const updateCooldown = () => {
      setAttackCooldown(getAttackCooldown());
    };
    updateCooldown();
    const interval = setInterval(updateCooldown, 1000); // Обновляем каждую секунду
    return () => clearInterval(interval);
  }, [getAttackCooldown]);

  // Coordinate mapping for buildings (Percentage based)
  const buildingTriggers = [
    { id: 'castle', name: t.base.castle, top: '35%', left: '50%', icon: '🏰' },
    { id: 'mageTower', name: t.base.mageTower, top: '37%', left: '80%', icon: '🧙‍♂️' },
    { id: 'barracks', name: t.base.barracks, top: '47%', left: '32%', icon: '⚔️' },
    { id: 'forge', name: t.base.forge, top: '47%', left: '11%', icon: '⚒️' },
    { id: 'temple', name: t.base.temple, top: '44%', left: '65%', icon: '⛪' },
    { id: 'raid', name: t.base.raid, top: '62%', left: '38%', icon: '🚪' },
  ];

  const liberationPercent = Math.floor(liberation);

  // Вспомогательная функция для туториала
  const getNextPhase = (currentPhase: string | null): 'resources' | 'taxHouse' | 'barracks' | 'raid' | 'battle' | null => {
    switch (currentPhase) {
      case 'resources': return 'taxHouse';
      case 'taxHouse': return 'barracks';
      case 'barracks': return 'raid';
      case 'raid': return 'battle';
      case 'battle': return null;
      default: return null;
    }
  };

  return (
    <div className="bg-black overflow-hidden flex flex-col sm:flex-row font-pixel select-none text-white relative" style={{
      height: 'calc(100vh - env(safe-area-inset-top, 0) - env(safe-area-inset-bottom, 0))',
      width: 'calc(100vw - env(safe-area-inset-left, 0) - env(safe-area-inset-right, 0))',
      paddingTop: 'env(safe-area-inset-top, 0)',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      paddingLeft: 'env(safe-area-inset-left, 0)',
      paddingRight: 'env(safe-area-inset-right, 0)',
    }}>

      {/* Top Panel (Mobile) / Left Sidebar (Desktop): Resources */}
      <div className="absolute top-0 left-0 sm:h-full h-auto z-40 bg-black/95 p-1.5 sm:p-2 md:p-3 lg:p-4 pointer-events-none w-full sm:w-[140px] md:w-[180px] lg:w-[220px]">
        <div className="flex flex-col gap-1.5 sm:gap-2 pointer-events-auto h-full overflow-y-auto">
          {/* Title and Back Button */}
          <div className="flex items-center gap-1 sm:gap-2 mb-1.5 sm:mb-2">
            <button
              onClick={() => navigate('/game-start')}
              className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded cursor-pointer transition-colors flex-shrink-0 text-xs sm:text-sm"
            >
              ←
            </button>
            <h1 className="text-[8px] sm:text-[10px] md:text-sm lg:text-base font-bold tracking-widest text-yellow-500 uppercase leading-tight">
              {t.base.citadel}
            </h1>
          </div>

          {/* Liberation Progress */}
          <div className="flex flex-col gap-0.5 sm:gap-1 w-full mb-1.5 sm:mb-2">
            <div className="flex justify-between text-[6px] sm:text-[7px] md:text-[8px] tracking-widest text-white/50 uppercase">
              <span className="truncate">{t.base.liberation}</span>
              <span className="flex-shrink-0 ml-1">{liberationPercent}%</span>
            </div>
            <div className="h-1.5 sm:h-2 w-full bg-white/5 border border-white/10 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${liberationPercent}%` }}
                className="h-full bg-gradient-to-r from-yellow-800 to-yellow-500 shadow-[0_0_10px_rgba(234,179,8,0.5)]"
              />
            </div>
          </div>

          {/* Resources - Vertical layout (portrait) */}
          <div className="flex flex-col sm:hidden gap-1 bg-black/60 backdrop-blur-md p-2 border border-white/10 rounded-sm">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {/* Column 1 */}
              <div className="flex flex-col gap-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-[8px] text-white/30 uppercase">{t.base.gold}</span>
                  <span className="text-[10px] text-yellow-500 flex-shrink-0 ml-2">🪙 {gold}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[8px] text-white/30 uppercase">{t.base.metal}</span>
                  <span className="text-[10px] text-gray-300 flex-shrink-0 ml-2">⚒️ {resources.metal}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[8px] text-white/30 uppercase">{t.base.wood}</span>
                  <span className="text-[10px] text-orange-400 flex-shrink-0 ml-2">🌲 {resources.wood}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[8px] text-white/30 uppercase">{t.base.leather}</span>
                  <span className="text-[10px] text-amber-600 flex-shrink-0 ml-2">🦌 {resources.leather || 0}</span>
                </div>
              </div>
              {/* Column 2 */}
              <div className="flex flex-col gap-1.5">
                <div className="flex justify-between items-center">
                  <span className="text-[8px] text-white/30 uppercase">{t.base.crystals}</span>
                  <span className="text-[10px] text-cyan-400 flex-shrink-0 ml-2">💎 {resources.crystals}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[8px] text-white/30 uppercase">{t.base.fighters}</span>
                  <span className="text-[10px] text-green-500 flex-shrink-0 ml-2">👥 {fighters.length}/{maxFighters}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[8px] text-white/30 uppercase">{t.base.greenPotion}</span>
                  <span className="text-[10px] text-green-400 flex-shrink-0 ml-2">💚 {potions?.greenPotion || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[8px] text-white/30 uppercase">{t.base.redPotion}</span>
                  <span className="text-[10px] text-red-400 flex-shrink-0 ml-2">❤️ {potions?.redPotion || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Resources - Horizontal layout (landscape) */}
          <div className="hidden sm:flex flex-col gap-1 sm:gap-1.5 bg-black/60 backdrop-blur-md p-1.5 sm:p-2 md:p-3 border border-white/10 rounded-sm">
            <div className="flex justify-between items-center">
              <span className="text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] text-white/30 uppercase truncate">{t.base.gold}</span>
              <span className="text-[9px] sm:text-xs md:text-sm text-yellow-500 flex-shrink-0 ml-1">🪙 {gold}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] text-white/30 uppercase truncate">{t.base.metal}</span>
              <span className="text-[9px] sm:text-xs md:text-sm text-gray-300 flex-shrink-0 ml-1">⚒️ {resources.metal}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] text-white/30 uppercase truncate">{t.base.wood}</span>
              <span className="text-[9px] sm:text-xs md:text-sm text-orange-400 flex-shrink-0 ml-1">🌲 {resources.wood}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] text-white/30 uppercase truncate">{t.base.crystals}</span>
              <span className="text-[9px] sm:text-xs md:text-sm text-cyan-400 flex-shrink-0 ml-1">💎 {resources.crystals}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] text-white/30 uppercase truncate">{t.base.leather}</span>
              <span className="text-[9px] sm:text-xs md:text-sm text-amber-600 flex-shrink-0 ml-1">🦌 {resources.leather || 0}</span>
            </div>
            <div className="flex justify-between items-center pt-0.5 sm:pt-1 border-t border-white/10">
              <span className="text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] text-white/30 uppercase truncate">{t.base.fighters}</span>
              <span className="text-[9px] sm:text-xs md:text-sm text-green-500 flex-shrink-0 ml-1">👥 {fighters.length}/{maxFighters}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] text-white/30 uppercase truncate">{t.base.greenPotion}</span>
              <span className="text-[9px] sm:text-xs md:text-sm text-green-400 flex-shrink-0 ml-1">💚 {potions?.greenPotion || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-[6px] sm:text-[7px] md:text-[8px] lg:text-[9px] text-white/30 uppercase truncate">{t.base.redPotion}</span>
              <span className="text-[9px] sm:text-xs md:text-sm text-red-400 flex-shrink-0 ml-1">❤️ {potions?.redPotion || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main View: The Castle Illustration */}
      <div className="flex-1 relative bg-zinc-950 overflow-hidden flex items-center justify-center pt-[200px] sm:pt-0 sm:ml-[140px] md:ml-[180px] lg:ml-[220px]">
        <div className="relative aspect-[3/4] h-full max-w-full">
          <img
            src="./sprites/bases/base_humans_internal.jpg"
            alt="Human Base Interior"
            className="h-full w-auto object-contain block"
          />

          {/* Tax House Icon (правый нижний угол, выше - где показано стрелкой) */}
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setShowTaxHouse(true)}
            className="absolute z-30 group"
            style={{
              bottom: '20%',
              right: '15%',
              width: '48px',
              height: '48px',
            }}
          >
            <div className="w-full h-full flex items-center justify-center drop-shadow-[0_0_15px_rgba(255,215,0,0.8)] filter brightness-110 relative">
              <img
                src="./sprites/buildings/house.png"
                alt="Дом"
                className="w-full h-full object-contain"
                onError={(e) => {
                  const el = e.target as HTMLElement;
                  el.style.display = 'none';
                  const parent = el.parentElement;
                  if (parent && !parent.getAttribute('data-has-emoji')) {
                    parent.setAttribute('data-has-emoji', 'true');
                    parent.insertAdjacentHTML('beforeend', '<span class="text-3xl filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]">🏠</span>');
                  }
                }}
              />
              {/* Таймер или монетка над иконкой */}
              {taxCooldown > 0 ? (
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black/90 border border-yellow-500 rounded px-2 py-1 text-[10px] text-yellow-400 whitespace-nowrap z-50">
                  {Math.floor(taxCooldown / 60)}:{(taxCooldown % 60).toString().padStart(2, '0')}
                </div>
              ) : (
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-50 animate-pulse">
                  <span className="text-2xl">🪙</span>
                </div>
              )}
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                <div className="bg-black/90 border border-yellow-500 rounded px-2 py-1 text-[10px] text-yellow-400 whitespace-nowrap">
                  {t.base.taxHouse}
                </div>
              </div>
              <div className={`absolute inset-0 rounded-full animate-ping blur-md -z-10 bg-white/20`} />
            </div>
          </motion.button>

          {/* Orc Attack Icon (левый нижний угол) */}
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setShowOrcAttack(true)}
            className="absolute z-30 group"
            style={{
              bottom: '20%',
              left: '10%',
              width: '48px',
              height: '48px',
            }}
          >
            <div className="w-full h-full flex items-center justify-center drop-shadow-[0_0_15px_rgba(255,0,0,0.8)] filter brightness-110 relative">
              <img
                src="./sprites/buildings/orc.png"
                alt="Нападение орков"
                className="w-full h-full object-contain"
                onError={(e) => {
                  const el = e.target as HTMLElement;
                  el.style.display = 'none';
                  const parent = el.parentElement;
                  if (parent && !parent.getAttribute('data-has-emoji')) {
                    parent.setAttribute('data-has-emoji', 'true');
                    parent.insertAdjacentHTML('beforeend', '<span class="text-4xl filter drop-shadow-[0_0_8px_rgba(255,0,0,0.8)]">👹</span>');
                  }
                }}
              />
              {/* Таймер над иконкой */}
              {attackCooldown > 0 ? (
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black/90 border border-red-500 rounded px-2 py-1 text-[10px] text-red-400 whitespace-nowrap z-50">
                  {Math.floor(attackCooldown / 60)}:{(attackCooldown % 60).toString().padStart(2, '0')}
                </div>
              ) : (
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-red-800 border border-red-600 rounded px-2 py-1 text-[10px] text-white whitespace-nowrap z-50 animate-pulse">
                  {t.base.attackAlert}
                </div>
              )}
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                <div className="bg-black/90 border border-red-500 rounded px-2 py-1 text-[10px] text-red-400 whitespace-nowrap">
                  {t.base.orcAttack}
                </div>
              </div>
              <div className={`absolute inset-0 rounded-full animate-ping blur-md -z-10 bg-red-500/20`} />
            </div>
          </motion.button>

          {/* Building Trigger Icons */}
          {buildingTriggers.map((trigger) => (
            <motion.button
              key={trigger.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => {
                if (trigger.id === 'raid') setShowRaid(true);
                else setSelectedBuilding(trigger.id);
              }}
              className="absolute z-30 group"
              style={{
                top: trigger.top,
                left: trigger.left,
                width: '48px',
                height: '48px',
                transform: 'translate(-50%, -50%)'
              }}
            >
              {/* The "Open Lock" or "Building Icon" */}
              <div className={`w-full h-full flex items-center justify-center drop-shadow-[0_0_15px_rgba(255,215,0,0.8)] filter brightness-110 relative`}>
                {(trigger.id === 'castle' || trigger.id === 'raid' || (buildings as any)[trigger.id]) ? (
                  <div className="w-full h-full flex items-center justify-center relative">
                    <img
                      src={`./sprites/buildings/${trigger.id}.png`}
                      alt={trigger.name}
                      className="w-full h-full object-contain filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                      onError={(e) => {
                        (e.target as any).style.display = 'none';
                        const el = (e.target as any).parentElement;
                        if (!el.getAttribute('data-has-emoji')) {
                          el.setAttribute('data-has-emoji', 'true');
                          el.insertAdjacentHTML('beforeend', `<span class="text-3xl filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]">${trigger.icon}</span>`);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <img
                    src="./sprites/ui/open_lock.png"
                    alt="Locked"
                    className="w-full h-full opacity-60 grayscale"
                  />
                )}
              </div>

              {/* Hover Label */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-black/80 px-2 py-1 border border-yellow-600/50 rounded-sm">
                <span className="text-[8px] text-yellow-500 tracking-widest uppercase">
                  {(trigger.id === 'castle' || trigger.id === 'raid' || (buildings as any)[trigger.id]) ? trigger.name : `${t.common.build}: ${trigger.name}`}
                </span>
              </div>

              {/* Pulsing Beacon */}
              <div className={`absolute inset-0 rounded-full animate-ping blur-md -z-10 ${(trigger.id === 'castle' || trigger.id === 'raid' || (buildings as any)[trigger.id]) ? 'bg-white/20' : 'bg-yellow-400/20'}`} />
            </motion.button>
          ))}

          {/* Tutorial UI - Placed inside the aspect container to match coordinates */}
          {!hasSeenBaseTutorial && (
            <div className="absolute inset-0 z-[100]">
              <BaseTutorial onEnd={() => setHasSeenBaseTutorial(true)} />
            </div>
          )}

          {/* Extended Tutorial */}
          {hasSeenBaseTutorial && extendedTutorialPhase && extendedTutorialPhase !== 'battle' && extendedTutorialStep >= 0 && (
            <ExtendedTutorial
              phase={extendedTutorialPhase}
              currentStep={extendedTutorialStep}
              onStepComplete={() => {
                // Получаем количество шагов для текущего этапа
                const getStepCount = (p: string | null): number => {
                  switch (p) {
                    case 'resources': return 8;
                    case 'taxHouse': return 2;
                    case 'barracks': return 9;
                    case 'raid': return 3;
                    case 'battle': return 3;
                    default: return 0;
                  }
                };
                const maxSteps = getStepCount(extendedTutorialPhase);
                if (extendedTutorialStep < maxSteps - 1) {
                  setExtendedTutorialStep(extendedTutorialStep + 1);
                } else {
                  // Переход к следующему этапу
                  const nextPhase = getNextPhase(extendedTutorialPhase);
                  if (nextPhase) {
                    setExtendedTutorialPhase(nextPhase);
                    setExtendedTutorialStep(0);
                  } else {
                    setExtendedTutorialPhase(null);
                    setExtendedTutorialStep(-1);
                  }
                }
              }}
              onPhaseComplete={() => {
                // Переход к следующему этапу
                const nextPhase = getNextPhase(extendedTutorialPhase);
                if (nextPhase) {
                  setExtendedTutorialPhase(nextPhase);
                  setExtendedTutorialStep(0);
                } else {
                  setExtendedTutorialPhase(null);
                  setExtendedTutorialStep(-1);
                }
              }}
            />
          )}
        </div>

        {/* Vignette Overlay */}
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.5)_100%)]"></div>
      </div>

      {/* Modals Layer */}
      <AnimatePresence>
        {selectedBuilding === 'castle' && (
          <BuildingModal
            title={t.modals.castle.title}
            onClose={() => setSelectedBuilding(null)}
            content={
              <div className="text-white font-pixel text-xs space-y-4 p-4">
                <div className="text-yellow-500 border-b border-white/10 pb-2 mb-2 uppercase tracking-widest text-[10px]">{t.modals.castle.treasury}</div>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between"><span>🪙 {t.modals.castle.treasury}:</span> <span className="text-yellow-500">{gold} {t.base.gold}</span></div>
                  <div className="flex justify-between"><span>📊 {t.modals.castle.loyalty}</span> <span className="text-green-500">{liberationPercent}%</span></div>
                  <div className="flex justify-between"><span>👥 {t.modals.castle.army}</span> <span>{fighters.length}/{maxFighters} {t.base.fighters}</span></div>
                </div>

                <div className="bg-black/40 border border-white/10 p-2 rounded">
                  <div className="text-[8px] text-yellow-600 mb-2 uppercase tracking-tighter">{t.modals.castle.conquestDiary}</div>
                  <div className="grid grid-cols-5 gap-1">
                    {[1, 2, 3, 4, 5].map(depth => (
                      <div key={depth} className="flex flex-col items-center p-1 border border-white/5 bg-white/5">
                        <div className="text-[6px] text-white/40 mb-1">{t.modals.castle.depth}{depth}</div>
                        <div className="text-[8px] text-yellow-500 font-bold">{raidStats[depth] || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-white/40 text-[7px] mt-4 leading-relaxed uppercase italic">
                  {t.modals.castle.description}
                </p>
              </div>
            }
          />
        )}

        {selectedBuilding === 'barracks' && (
          <BarracksModal onClose={() => setSelectedBuilding(null)} />
        )}

        {selectedBuilding === 'forge' && (
          <ForgeModal onClose={() => setSelectedBuilding(null)} />
        )}

        {selectedBuilding === 'temple' && (
          <TempleModal onClose={() => setSelectedBuilding(null)} />
        )}

        {selectedBuilding === 'mageTower' && (
          <MageTowerModal onClose={() => setSelectedBuilding(null)} />
        )}

        {showRaid && (
          <RaidModal onClose={() => setShowRaid(false)} />
        )}

        {/* Tax House Modal */}
        {showTaxHouse && (
          <BuildingModal
            title={t.modals.taxHouse.title}
            onClose={() => setShowTaxHouse(false)}
            data-tutorial="taxHouseModal"
            content={
              <div className="text-white font-pixel text-xs">
                <div className="mb-4 text-gray-300">
                  {t.modals.taxHouse.description}
                </div>

                <div className="mb-4 bg-black/40 border border-white/10 p-3 rounded">
                  <div className="text-yellow-500 mb-2 text-sm font-bold">{t.modals.taxHouse.taxSize}</div>
                  <div className="text-[10px] space-y-1">
                    <div className={liberationPercent < 20 ? 'text-yellow-400' : 'text-gray-500'}>
                      0-20% {t.modals.taxHouse.liberationRange} <span className="text-yellow-500">20 {t.base.gold}</span>
                    </div>
                    <div className={liberationPercent >= 20 && liberationPercent < 40 ? 'text-yellow-400' : 'text-gray-500'}>
                      20-40% {t.modals.taxHouse.liberationRange} <span className="text-yellow-500">40 {t.base.gold}</span>
                    </div>
                    <div className={liberationPercent >= 40 && liberationPercent < 60 ? 'text-yellow-400' : 'text-gray-500'}>
                      40-60% {t.modals.taxHouse.liberationRange} <span className="text-yellow-500">80 {t.base.gold}</span>
                    </div>
                    <div className={liberationPercent >= 60 && liberationPercent < 80 ? 'text-yellow-400' : 'text-gray-500'}>
                      60-80% {t.modals.taxHouse.liberationRange} <span className="text-yellow-500">120 {t.base.gold}</span>
                    </div>
                    <div className={liberationPercent >= 80 ? 'text-yellow-400' : 'text-gray-500'}>
                      80-100% {t.modals.taxHouse.liberationRange} <span className="text-yellow-500">200 {t.base.gold}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-[10px] text-gray-400">
                      {t.modals.taxHouse.currentLiberation} <span className="text-yellow-500">{liberationPercent}%</span>
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1">
                      {t.modals.taxHouse.currentTax} <span className="text-yellow-500">{getTaxAmount()} {t.base.gold}</span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    data-tutorial="collectTaxButton"
                    onClick={() => {
                      const result = collectTaxes();
                      if (result.success) {
                        setShowTaxHouse(false);
                        // Можно показать уведомление о получении золота
                      } else {
                        alert(result.message);
                      }
                    }}
                    disabled={taxCooldown > 0}
                    className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all ${taxCooldown > 0
                        ? 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-yellow-700 hover:bg-yellow-600 border-2 border-yellow-500 text-white shadow-[0_4px_0_0_rgba(234,179,8,1)] active:shadow-none active:translate-y-1'
                      }`}
                  >
                    {taxCooldown > 0
                      ? `${t.modals.taxHouse.collectTaxesCooldown} (${Math.floor(taxCooldown / 60)}:${(taxCooldown % 60).toString().padStart(2, '0')})`
                      : `${t.modals.taxHouse.collectTaxesAmount} (+${getTaxAmount()} ${t.base.gold})`
                    }
                  </button>
                </div>
              </div>
            }
          />
        )}

        {/* Orc Attack Modal */}
        {showOrcAttack && (
          <BuildingModal
            title={t.modals.orcAttack.title}
            onClose={() => setShowOrcAttack(false)}
            content={
              <div className="text-white font-pixel text-xs space-y-4 max-h-[85vh] overflow-y-auto">
                <div className="text-white/60 text-[8px] mb-4 uppercase">
                  {t.modals.orcAttack.description}
                </div>

                <div className="bg-black/40 border border-red-500/50 p-3 rounded">
                  <div className="text-red-400 mb-2 text-sm font-bold">{t.modals.orcAttack.currentAttack}</div>
                  <div className="text-[10px] space-y-1">
                    <div className="text-white/80">
                      {t.modals.orcAttack.liberationPercent} <span className="text-yellow-500">{liberationPercent}%</span>
                    </div>
                    {(() => {
                      const info = getAttackInfo();
                      const formatComposition = () => {
                        const parts: string[] = [];
                        if (info.composition.young > 0) {
                          parts.push(`${info.composition.young} ${t.modals.orcAttack.youngOrc}`);
                        }
                        if (info.composition.fighter > 0) {
                          parts.push(`${info.composition.fighter} ${t.modals.orcAttack.fighterOrc}`);
                        }
                        if (info.composition.veteran > 0) {
                          parts.push(`${info.composition.veteran} ${t.modals.orcAttack.veteranOrc}`);
                        }
                        return parts.join(' + ');
                      };
                      return (
                        <>
                          <div className="text-white/80">
                            {t.modals.orcAttack.orcCount} <span className="text-red-400 font-bold">{info.count}</span>
                          </div>
                          <div className="text-white/80">
                            {t.modals.orcAttack.composition} <span className="text-gray-400">{formatComposition()}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="text-[10px] text-gray-400">
                      {attackCooldown > 0 ? (
                        <>{t.modals.orcAttack.attackIn} {Math.floor(attackCooldown / 60)}:{(attackCooldown % 60).toString().padStart(2, '0')}</>
                      ) : (
                        <span className="text-red-400 font-bold">{t.modals.orcAttack.attackReady}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-black/40 border border-white/10 p-3 rounded">
                  <div className="text-yellow-500 mb-3 text-sm font-bold">{t.modals.orcAttack.attackTable}</div>
                  <div className="text-[8px] space-y-2">
                    <div className={`p-2 rounded ${liberationPercent < 10 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range1}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range1Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 10 && liberationPercent < 20 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range2}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range2Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 20 && liberationPercent < 30 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range3}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range3Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 30 && liberationPercent < 40 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range4}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range4Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 40 && liberationPercent < 50 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range5}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range5Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 50 && liberationPercent < 60 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range6}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range6Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 60 && liberationPercent < 70 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range7}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range7Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 70 && liberationPercent < 80 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range8}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range8Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 80 && liberationPercent < 90 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range9}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range9Desc}</div>
                    </div>
                    <div className={`p-2 rounded ${liberationPercent >= 90 ? 'bg-red-900/30 border border-red-500' : 'bg-black/20'}`}>
                      <div className="font-bold text-yellow-400">{t.modals.orcAttack.range10}</div>
                      <div className="text-white/70 mt-1">{t.modals.orcAttack.range10Desc}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-black/40 border border-blue-500/50 p-3 rounded">
                  <div className="text-blue-400 mb-2 text-sm font-bold">{t.modals.orcAttack.defense}</div>
                  <div className="text-[10px] text-white/70 space-y-1">
                    <div>• {t.modals.orcAttack.maxFighters}</div>
                    <div>• {t.modals.orcAttack.mapSize}</div>
                    <div>• {t.modals.orcAttack.attackFrequency}</div>
                  </div>
                </div>

                <div className="bg-black/40 border border-red-500/50 p-3 rounded">
                  <div className="text-red-400 mb-2 text-sm font-bold">{t.modals.orcAttack.warning}</div>
                  <div className="text-[10px] text-white/70 space-y-1">
                    <div>• {t.modals.orcAttack.lossPenalty}</div>
                  </div>
                </div>

                {attackCooldown === 0 && (
                  <div className="flex justify-center mt-4 pt-3 border-t border-white/10">
                    <button
                      onClick={() => {
                        const enemies = generateBaseAttackEnemies();
                        const selectedFighters = fighters.filter(f => f.health > 0).slice(0, 15);
                        if (selectedFighters.length === 0) {
                          alert(t.modals.orcAttack.noFighters);
                          return;
                        }
                        resetAttackTimer();
                        navigate('/battle', {
                          state: {
                            fighters: selectedFighters,
                            isRaid: false,
                            isBaseAttack: true,
                            enemies: enemies,
                          }
                        });
                      }}
                      className="px-6 py-3 text-[12px] font-bold uppercase tracking-wider bg-red-700 hover:bg-red-600 border-red-500 shadow-[0_4px_0_0_rgba(127,29,29,1)] active:shadow-none active:translate-y-1 transition-all"
                    >
                      {t.modals.orcAttack.startDefense}
                    </button>
                  </div>
                )}
              </div>
            }
          />
        )}
      </AnimatePresence>

      {/* Global hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 bg-black/60 backdrop-blur-sm px-4 py-2 border border-white/10 rounded-full">
        <p className="text-[7px] text-white/40 uppercase tracking-[0.3em]">{t.base.buildHint}</p>
      </div>

      <style>{`
                @font-face {
                    font-family: 'Press Start 2P';
                    src: url('https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap');
                }
            `}</style>
    </div>
  );
};
